import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { GlowButton } from '@/components/brand/GlowButton';
import { formatGBP } from '@/lib/utils';
import { usePaymentStatusPolling } from '@/hooks/usePaymentStatusPolling';

export default function PaymentSuccessPage() {
    const [sp] = useSearchParams();
    const sessionId = sp.get('session_id');
    const state = usePaymentStatusPolling(sessionId);
    const view = pickResultView(state);

    return (
        <div className="bg-la-canvas text-la-text min-h-screen">
            <Navbar />
            <main className="pt-32 pb-24">
                <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
                    <div className="panel rounded-la p-10 sm:p-12" data-testid="payment-result">
                        {view}
                        <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
                            <GlowButton as={Link} to="/" variant="gold">Back to Home <ArrowRight className="h-4 w-4" /></GlowButton>
                            <GlowButton as={Link} to="/contact" variant="ghost">Contact the firm</GlowButton>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function pickResultView(state) {
    if (state.loading) return <PaymentVerifyingView />;
    if (state.status === 'paid') return <PaymentPaidView payment={state.payment} />;
    if (state.status === 'failed' || state.status === 'expired') return <PaymentFailedView />;
    return <PaymentProcessingView />;
}

const PaymentVerifyingView = () => (
    <>
        <Loader2 className="mx-auto h-10 w-10 text-la-gold animate-spin" />
        <h1 className="mt-6 font-display text-[28px] text-la-text">Verifying your payment…</h1>
        <p className="mt-2 text-la-text2">This usually takes a few seconds.</p>
    </>
);

const PaymentProcessingView = () => (
    <>
        <Loader2 className="mx-auto h-10 w-10 text-la-gold animate-spin" />
        <h1 className="mt-6 font-display text-[28px] text-la-text">Processing your payment</h1>
        <p className="mt-4 text-[15px] text-la-text2 leading-relaxed">If this takes longer than expected, please check your email for a confirmation receipt.</p>
    </>
);

const PaymentFailedView = () => (
    <>
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-300" />
        <h1 className="mt-6 font-display text-[28px] text-la-text">Payment Not Completed</h1>
        <p className="mt-4 text-[15px] text-la-text2 leading-relaxed">No charge was made. You may try again or contact the firm.</p>
    </>
);

const PaymentPaidView = ({ payment }) => {
    const reference = payment?.metadata?.payment_reference || payment?.metadata?.reference;
    return (
        <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-la-gold" />
            <h1 className="mt-6 font-display text-[28px] sm:text-[32px] text-la-text">Payment Received</h1>
            <p className="mt-4 text-[15px] text-la-text2 leading-relaxed max-w-md mx-auto">
                Thank you. Your payment has been successfully received and allocated against your reference number.
            </p>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4 text-left max-w-md mx-auto">
                {reference && (
                    <div>
                        <div className="meta-eyebrow text-la-text3">Reference</div>
                        <div className="mt-1 text-[14px] text-la-text">{reference}</div>
                    </div>
                )}
                {payment?.amount && (
                    <div>
                        <div className="meta-eyebrow text-la-text3">Amount</div>
                        <div className="mt-1 text-[14px] text-la-text">{formatGBP(payment.amount)}</div>
                    </div>
                )}
            </div>
        </>
    );
};
